
// client/src/components/owner/TenantAdd.jsx  (patched)
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

function TenantAdd() {
  const location = useLocation();
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'http://localhost:5000';

  const [ownerData] = useState(() => {
    const stateData = location.state;
    const localData = localStorage.getItem('ownerData');
    return stateData || (localData ? JSON.parse(localData) : null);
  });

  const [formState, setFormState] = useState({
    tenantname: '', // optional: override company name for this tenant
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    email: '',   // optional tenant contact email
    logo: null,
    waiver: null,
  });

  const [registerStatus, setRegisterStatus] = useState('');
  const [emailError, setEmailError] = useState('');
  const [preview, setPreview] = useState({ logo: null, waiver: null });
  const [submitting, setSubmitting] = useState(false);

  // Subscription plans
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState('');
  const [selectedPlanId, setSelectedPlanId] = useState(null);

  useEffect(() => {
    if (!ownerData?.id) {
      setRegisterStatus('Owner ID is missing. Please log in again.');
      navigate('/ownerlogin');
    }
  }, [ownerData, navigate]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setPlansLoading(true);
        const res = await axios.get(`${baseUrl}/api/billing/plans`);
        if (cancelled) return;
        const list = res.data?.plans || [];
        setPlans(list);
        // Default to the cheapest plan for convenience
        if (list.length > 0) {
          const cheapest = [...list].sort(
            (a, b) => Number(a.price_monthly) - Number(b.price_monthly)
          )[0];
          setSelectedPlanId(cheapest.id);
        }
      } catch (err) {
        if (cancelled) return;
        console.error('Failed to load plans:', err);
        setPlansError('Could not load subscription plans. You can pick one later.');
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [baseUrl]);

  if (!ownerData) return <p>Loading owner info...</p>;

  const validateEmail = (value) => {
    if (!value.trim()) {
      setEmailError('');
      return true; // optional field
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      setEmailError('Please enter a valid email (no spaces, must contain @ and end with a domain)');
      return false;
    }
    setEmailError('');
    return true;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (name === 'email' && value.trim()) validateEmail(value);
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (files.length > 0) {
      setFormState((prev) => ({ ...prev, [name]: files[0] }));
      setPreview((prev) => ({
        ...prev,
        [name]: URL.createObjectURL(files[0]),
      }));
    }
  };

  const registerTenant = async (e) => {
    e.preventDefault();

    if (!ownerData?.id) {
      setRegisterStatus('Owner ID is missing. Please log in again.');
      return;
    }

    if (formState.email && !validateEmail(formState.email)) {
      setRegisterStatus('Please enter a valid email address.');
      return;
    }

    const formData = new FormData();
    formData.append('ownerid', ownerData.id);

    // Only send address/contact + files; server will read the owner profile directly
    if (formState.tenantname) formData.append('tenantname', formState.tenantname);
    formData.append('address1', formState.address1);
    if (formState.address2) formData.append('address2', formState.address2);
    formData.append('city', formState.city);
    formData.append('state', formState.state);
    if (formState.zip) formData.append('zip', formState.zip);
    if (formState.email) formData.append('email', formState.email);

    if (formState.logo) formData.append('logo', formState.logo);
    if (formState.waiver) formData.append('waiver', formState.waiver);

    try {
      setSubmitting(true);
      const response = await axios.post(`${baseUrl}/api/tenants/registertenant`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const newTenantId = response.data?.tenantId;
      setRegisterStatus(response.data.message || 'Tenant registered successfully!');

      // If the user picked a plan, kick off the subscription immediately and
      // route into payment-methods so they can complete it. If something goes
      // wrong here we still want them to be able to log in, so we soft-fail.
      if (newTenantId && selectedPlanId) {
        const chosenPlan = plans.find((p) => p.id === selectedPlanId);
        try {
          await axios.post(`${baseUrl}/api/billing/subscription/${newTenantId}`, {
            planId: selectedPlanId,
          });
          navigate(`/${newTenantId}/billing/payment-methods`, {
            state: {
              planId: selectedPlanId,
              planName: chosenPlan?.name,
              planPrice: chosenPlan ? Number(chosenPlan.price_monthly) : undefined,
              purpose: 'subscription_payment',
              message: chosenPlan?.trial_days > 0
                ? `Welcome! Your ${chosenPlan.trial_days}-day free trial of ${chosenPlan.name} has started. Add a payment method to continue after the trial.`
                : `Subscribed to ${chosenPlan?.name || 'your plan'}. Add a payment method to activate.`,
            },
          });
          return;
        } catch (subErr) {
          console.error('Subscription creation failed:', subErr);
          setRegisterStatus(
            'Tenant created, but we could not start your subscription. You can choose a plan from the Billing page after logging in.'
          );
        }
      }

      setTimeout(() => navigate('/login'), 1200);
    } catch (error) {
      console.error('Error registering tenant:', error.response?.data || error.message);
      setRegisterStatus(error.response?.data?.message || 'Failed to register tenant.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatLimit = (value, unlimited) => (unlimited ? 'Unlimited' : value);

  return (
    <div className="col-md-4">
      <div className="card">
        <div className="card-body">
          <h4 className="hdr hdr--primary hdr--lg">Create Tenant</h4>
          <form onSubmit={registerTenant}>
            <div className="form-group">
              <label htmlFor="tenantname">Tenant Name (optional):</label>
              <input
                className="textInput"
                type="text"
                name="tenantname"
                value={formState.tenantname}
                onChange={handleInputChange}
                placeholder={ownerData.companyname || 'Defaults to company name'}
              />
            </div>

            <div className="form-group">
              <label htmlFor="email">Tenant Contact Email (optional):</label>
              <input
                className="textInput"
                type="email"
                name="email"
                value={formState.email}
                onChange={handleInputChange}
                placeholder="someone@yourtenant.com"
              />
              {emailError && <p className="text-red-600">{emailError}</p>}
            </div>

            <div className="form-group">
              <label htmlFor="address1">Address 1:</label>
              <input
                className="textInput"
                type="text"
                name="address1"
                value={formState.address1}
                onChange={handleInputChange}
                placeholder="Address 1"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="address2">Address 2:</label>
              <input
                className="textInput"
                type="text"
                name="address2"
                value={formState.address2}
                onChange={handleInputChange}
                placeholder="Address 2"
              />
            </div>

            <div className="form-group">
              <label htmlFor="city">City:</label>
              <input
                className="textInput"
                type="text"
                name="city"
                value={formState.city}
                onChange={handleInputChange}
                placeholder="City"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="state">State:</label>
              <input
                className="textInput"
                type="text"
                name="state"
                value={formState.state}
                onChange={handleInputChange}
                placeholder="State"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="zip">Zip:</label>
              <input
                className="textInput"
                type="text"
                name="zip"
                value={formState.zip}
                onChange={handleInputChange}
                placeholder="Zip"
              />
            </div>

            <div className="form-group">
              <label htmlFor="logo">Upload Logo:</label>
              <input type="file" name="logo" onChange={handleFileChange} />
              {preview.logo && <img src={preview.logo} alt="Logo Preview" style={{ width: '100px', marginTop: '10px' }} />}
            </div>

            <div className="form-group">
              <label htmlFor="waiver">Upload Waiver:</label>
              <input type="file" name="waiver" onChange={handleFileChange} />
              {preview.waiver && (
                <div style={{ marginTop: '10px' }}>
                  <p>{formState.waiver?.name}</p>
                </div>
              )}
            </div>

            <div className="form-group" style={{ marginTop: 24 }}>
              <label style={{ fontWeight: 600 }}>Choose a Subscription Plan</label>
              {plansLoading && <p style={{ color: '#aaa' }}>Loading plans...</p>}
              {plansError && <p style={{ color: '#dc3545' }}>{plansError}</p>}
              {!plansLoading && !plansError && plans.length === 0 && (
                <p style={{ color: '#aaa' }}>
                  No plans available right now — you can subscribe later from the Billing page.
                </p>
              )}
              {!plansLoading && plans.length > 0 && (
                <div style={{ display: 'grid', gap: 10, marginTop: 8 }}>
                  {plans.map((plan) => {
                    const isSelected = selectedPlanId === plan.id;
                    return (
                      <label
                        key={plan.id}
                        style={{
                          display: 'block',
                          border: `2px solid ${isSelected ? '#ff6800' : '#3d3d38'}`,
                          borderRadius: 8,
                          padding: 12,
                          cursor: 'pointer',
                          background: isSelected ? 'rgba(255,104,0,0.08)' : 'transparent',
                        }}
                      >
                        <input
                          type="radio"
                          name="subscriptionPlan"
                          value={plan.id}
                          checked={isSelected}
                          onChange={() => setSelectedPlanId(plan.id)}
                          style={{ marginRight: 8 }}
                        />
                        <strong>{plan.name}</strong>{' '}
                        <span style={{ color: '#ff6800', fontWeight: 600 }}>
                          ${Number(plan.price_monthly).toFixed(2)}/mo
                        </span>
                        {plan.trial_days > 0 && (
                          <span style={{ color: '#aaa', marginLeft: 8 }}>
                            • {plan.trial_days}-day free trial
                          </span>
                        )}
                        <div style={{ fontSize: '0.85rem', color: '#ccc', marginTop: 4 }}>
                          {formatLimit(plan.included_users, plan.is_unlimited_users)}{' '}
                          user{plan.is_unlimited_users || plan.included_users !== 1 ? 's' : ''} •{' '}
                          {formatLimit(plan.included_integrations, plan.is_unlimited_integrations)}{' '}
                          integrations •{' '}
                          {formatLimit(plan.included_posts_monthly, plan.is_unlimited_posts)} scheduled posts/mo
                        </div>
                      </label>
                    );
                  })}
                  <label style={{ display: 'block', color: '#aaa', marginTop: 4 }}>
                    <input
                      type="radio"
                      name="subscriptionPlan"
                      value=""
                      checked={selectedPlanId === null}
                      onChange={() => setSelectedPlanId(null)}
                      style={{ marginRight: 8 }}
                    />
                    Skip for now — choose a plan later from the Billing page
                  </label>
                </div>
              )}
            </div>

            <button className="btn btn-primary" type="submit" disabled={submitting}>
              {submitting ? 'Creating tenant...' : 'Register Tenant'}
            </button>
            <button onClick={() => navigate('/login')} className="btn btn-secondary" type="button">
                Login
            </button>
          </form>
          {registerStatus && <p>{registerStatus}</p>}
        </div>
      </div>
    </div>
  );
}

export default TenantAdd;
